import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import { trpc } from '@/lib/trpc'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useRef } from 'react'
import type { ScanFindingEventDTO } from '@deepguide-ai/types'
import {
  Github,
  ArrowRight,
  Loader2,
  Lock,
  Globe,
} from 'lucide-react'
import { toast } from 'sonner'
import { activeWorkspaceWithStorageAtom } from '@/stores/workspaceStore'
import { useAtom } from 'jotai'
import { ScreenataStepLayout } from '@/components/screenata/ScreenataStepLayout'
import { AnalysisDrawer } from '@/components/screenata/AnalysisDrawer'
import {
  Tags,
  TagsTrigger,
  TagsValue,
  TagsContent,
  TagsInput,
  TagsList,
  TagsEmpty,
  TagsGroup,
  TagsItem,
} from '@/components/kibo-ui/tags'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/screenata/connect-repo',
)({
  component: ConnectRepoPage,
})

function ConnectRepoPage() {
  const { workspaceId } = useParams({
    from: '/(protected)/workspaces/$workspaceId/screenata/connect-repo',
  })
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [workspace] = useAtom(activeWorkspaceWithStorageAtom)
  const [isInstalling, setIsInstalling] = useState(false)
  const [isTriggering, setIsTriggering] = useState(false)
  const [githubAuthIssue, setGitHubAuthIssue] = useState<string | null>(null)
  const [triggerHeartbeat, setTriggerHeartbeat] = useState(0)
  const previousRunIdRef = useRef<string | null>(null)
  const triggerStartedAtRef = useRef<number>(0)
  const terminalToastRunIdRef = useRef<string | null>(null)
  const [activeRunId, setActiveRunId] = useState<string | null>(null)
  const [analysisDraft, setAnalysisDraft] = useState('')
  const [analysisDraftSourceId, setAnalysisDraftSourceId] = useState<string | null>(null)
  const [analysisUserEdited, setAnalysisUserEdited] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const programQuery = useQuery(
    trpc.complianceProgram.getProgram.queryOptions({ framework: 'SOC2' }),
  )

  // GitHub App queries
  const isConfiguredQuery = useQuery(
    trpc.githubApp.isConfigured.queryOptions(),
  )

  const installUrlQuery = useQuery({
    ...trpc.githubApp.getInstallationUrl.queryOptions({
      workspaceId: workspace?.id,
    }),
    enabled: isConfiguredQuery.data === true && !!workspace?.id,
  })

  const installationsQuery = useQuery({
    ...trpc.githubApp.listInstallations.queryOptions(),
    enabled: isConfiguredQuery.data === true,
  })

  const connectedReposQuery = useQuery({
    ...trpc.githubApp.listConnectedRepositories.queryOptions({ workspaceId }),
    refetchInterval: 3000,
  })

  // Mutations
  const handleInstallationCallback = useMutation(
    trpc.githubApp.handleInstallationCallback.mutationOptions(),
  )

  const connectRepoMutation = useMutation(
    trpc.githubApp.connectRepository.mutationOptions(),
  )

  const triggerAnalysis = useMutation(
    trpc.complianceProgram.triggerCodebaseAnalysis.mutationOptions(),
  )

  const disconnectRepoMutation = useMutation(
    trpc.githubApp.disconnectRepository.mutationOptions(),
  )
  const validateRepoAuthMutation = useMutation(
    trpc.githubApp.validateRepositoryAuth.mutationOptions(),
  )

  const skipScanningMutation = useMutation(
    trpc.complianceProgram.skipScanning.mutationOptions(),
  )
  const updateContextMemoryMutation = useMutation(
    trpc.complianceProgram.updateContextMemory.mutationOptions(),
  )
  const findingsQuery = useQuery({
    ...trpc.complianceProgram.getScanFindings.queryOptions({ framework: 'SOC2' }),
    refetchInterval: (query) => {
      const data = query.state.data as { isTerminal?: boolean; phase?: string } | undefined
      if (isTriggering || triggerAnalysis.isPending) return 2000
      if (data && !data.isTerminal && data.phase && data.phase !== 'idle') return 2000
      return false
    },
  })

  const program = programQuery.data
  const installations = installationsQuery.data ?? []
  const connectedRepos = connectedReposQuery.data ?? []
  const hasInstallations = installations.length > 0
  const hasConnectedRepos = connectedRepos.length > 0
  const contextQuery = useQuery({
    ...trpc.complianceProgram.getContextMemory.queryOptions({ framework: 'SOC2' }),
    enabled: !!program,
    refetchInterval: (query) => {
      const data = query.state.data as { status?: string } | undefined
      if (data?.status === 'streaming') return 1000
      if (isTriggering || triggerAnalysis.isPending) return 2500
      if (data?.status === 'running') return 2500
      return false
    },
  })

  // Listen for GitHub App installation callback from popup
  useEffect(() => {
    if (!isInstalling) return

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return

      if (
        event.data?.type === 'github-app-installed' &&
        event.data?.installationId
      ) {
        handleInstallationCallback.mutate(
          {
            installationId: event.data.installationId,
            setupAction: event.data.setupAction,
            workspaceId: workspace?.id,
          },
          {
            onSuccess: () => {
              toast.success('GitHub App installed successfully')
              invalidateGitHubQueries()
              setIsInstalling(false)
            },
            onError: (err) => {
              toast.error('Installation failed', {
                description: err.message,
              })
              setIsInstalling(false)
            },
          },
        )
      }

      if (event.data?.type === 'github-app-installed' && event.data?.success) {
        invalidateGitHubQueries()
        setIsInstalling(false)
      }

      if (event.data?.type === 'github-app-error') {
        setIsInstalling(false)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [isInstalling])

  function invalidateGitHubQueries() {
    qc.invalidateQueries({
      queryKey: trpc.githubApp.listInstallations.queryKey(),
    })
    qc.invalidateQueries({
      queryKey: trpc.githubApp.listConnectedRepositories.queryKey(),
    })
  }

  const handleInstallGitHubApp = () => {
    if (!installUrlQuery.data) return
    setGitHubAuthIssue(null)
    setIsInstalling(true)

    const width = 800
    const height = 600
    const left = window.screenX + (window.innerWidth - width) / 2
    const top = window.screenY + (window.innerHeight - height) / 2

    const popup = window.open(
      installUrlQuery.data,
      'github-app-install',
      `width=${width},height=${height},left=${left},top=${top}`,
    )

    const checkPopup = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkPopup)
        setIsInstalling(false)
        invalidateGitHubQueries()
      }
    }, 1000)
  }

  const handleDisconnectRepo = (repositoryId: string) => {
    disconnectRepoMutation.mutate(
      { repositoryId, workspaceId },
      {
        onSuccess: () => {
          toast.success('Repository disconnected')
          invalidateGitHubQueries()
        },
        onError: (err) => {
          toast.error('Failed to disconnect repository', {
            description: err.message,
          })
        },
      },
    )
  }

  const handleConnectRepo = (repositoryId: string) => {
    connectRepoMutation.mutate(
      { repositoryId, workspaceId },
      {
        onSuccess: () => {
          toast.success('Repository connected')
          invalidateGitHubQueries()
        },
        onError: (err) => {
          toast.error('Failed to connect repository', {
            description: err.message,
          })
        },
      },
    )
  }

  const handleContinue = async () => {
    if (!program) return
    setIsTriggering(true)
    setDrawerOpen(true)
    previousRunIdRef.current = findingsQuery.data?.runId ?? null
    triggerStartedAtRef.current = Date.now()
    terminalToastRunIdRef.current = null
    setActiveRunId(null)

    try {
      let hasGitHubAuthIssue = false
      for (const repo of connectedRepos) {
        if (repo.private) {
          const authCheck = await validateRepoAuthMutation.mutateAsync({
            repositoryId: repo.id,
          }) as { ok: boolean; message?: string }

          if (!authCheck.ok) {
            hasGitHubAuthIssue = true
            const issueMessage =
              authCheck.message ??
              'GitHub App authentication is invalid. Please reconnect GitHub.'
            setGitHubAuthIssue(issueMessage)
            toast.error('GitHub reconnect required', {
              description: `${issueMessage} Private repositories need GitHub App auth.`,
            })
            break
          }
        }

        const repoUrl = `https://github.com/${repo.fullName}.git`
        const result = await triggerAnalysis.mutateAsync({
          programId: program.id,
          repositoryUrl: repoUrl,
          branch: repo.defaultBranch ?? 'main',
          repositoryName: repo.fullName,
        })
        const typedResult = result as {
          mode?: string
          authIssueCode?: string | null
          authIssueMessage?: string | null
        } | undefined
        if (typedResult?.authIssueCode === 'GITHUB_APP_AUTH_INVALID') {
          if (repo.private) {
            hasGitHubAuthIssue = true
            const issueMessage =
              typedResult.authIssueMessage ??
              'GitHub App authentication is invalid. Please reconnect GitHub.'
            setGitHubAuthIssue(issueMessage)
            toast.error('GitHub authentication expired', {
              description: `${issueMessage} Click "Reconnect GitHub" below.`,
            })
            break
          }
        }
      }

      await qc.invalidateQueries({
        queryKey: trpc.complianceProgram.getProgram.queryKey(),
      })

      if (hasGitHubAuthIssue) {
        setIsTriggering(false)
        return
      }
    } catch (e) {
      console.error('[ConnectRepo] Failed to trigger analysis:', e)
      setIsTriggering(false)
    } finally {
      await qc.invalidateQueries({
        queryKey: trpc.complianceProgram.getScanFindings.queryKey(),
      })
    }
  }

  useEffect(() => {
    if (!isTriggering) return
    const timer = window.setInterval(() => {
      setTriggerHeartbeat((value) => value + 1)
    }, 1000)
    return () => window.clearInterval(timer)
  }, [isTriggering])

  useEffect(() => {
    if (!isTriggering) return
    const currentRunId = findingsQuery.data?.runId ?? null
    const isNewRun = currentRunId !== null && currentRunId !== previousRunIdRef.current
    const isTerminal = !!findingsQuery.data?.isTerminal
    const elapsedMs = Date.now() - triggerStartedAtRef.current
    const latestEventTs = findingsQuery.data?.findings?.length
      ? new Date(findingsQuery.data.findings[findingsQuery.data.findings.length - 1].timestamp).getTime()
      : null
    const isRecentRunForThisTrigger =
      latestEventTs !== null && latestEventTs >= triggerStartedAtRef.current - 5000

    if (isNewRun && !activeRunId) {
      setActiveRunId(currentRunId)
    }
    if (!activeRunId && !isNewRun && currentRunId && elapsedMs > 5000 && isRecentRunForThisTrigger) {
      // Fallback: if query timing/race hid the "new run" transition, attach to latest run after grace period.
      setActiveRunId(currentRunId)
    }

    if (isNewRun && isTerminal) {
      setIsTriggering(false)
      if (currentRunId && terminalToastRunIdRef.current !== currentRunId) {
        terminalToastRunIdRef.current = currentRunId
        if (findingsQuery.data?.phase === 'complete') {
          toast.success('Analysis complete. Review the results below.')
        } else if (findingsQuery.data?.phase === 'failed') {
          toast.error('Analysis failed. Review the logs below.')
        } else if (findingsQuery.data?.phase === 'skipped') {
          toast.message('Analysis was skipped.')
        }
      }
      return
    }

    if (activeRunId && currentRunId === activeRunId && isTerminal) {
      setIsTriggering(false)
      if (currentRunId && terminalToastRunIdRef.current !== currentRunId) {
        terminalToastRunIdRef.current = currentRunId
        if (findingsQuery.data?.phase === 'complete') {
          toast.success('Analysis complete. Review the results below.')
        } else if (findingsQuery.data?.phase === 'failed') {
          toast.error('Analysis failed. Review the logs below.')
        } else if (findingsQuery.data?.phase === 'skipped') {
          toast.message('Analysis was skipped.')
        }
      }
      return
    }

    if (!activeRunId && currentRunId && isTerminal && elapsedMs > 2000 && isRecentRunForThisTrigger) {
      // Final safety net: backend completed but run-id synchronization was missed on client.
      setIsTriggering(false)
      if (terminalToastRunIdRef.current !== currentRunId) {
        terminalToastRunIdRef.current = currentRunId
        if (findingsQuery.data?.phase === 'complete') {
          toast.success('Analysis complete. Review the results below.')
        } else if (findingsQuery.data?.phase === 'failed') {
          toast.error('Analysis failed. Review the logs below.')
        } else if (findingsQuery.data?.phase === 'skipped') {
          toast.message('Analysis was skipped.')
        }
      }
    }
  }, [
    isTriggering,
    triggerHeartbeat,
    activeRunId,
    findingsQuery.data?.runId,
    findingsQuery.data?.isTerminal,
    findingsQuery.data?.phase,
  ])

  const hasCurrentRun = !!findingsQuery.data?.runId
  const isShowingActiveRun = !!activeRunId && findingsQuery.data?.runId === activeRunId
  const showLatestRun = !isTriggering && hasCurrentRun
  const shouldRenderRun = isShowingActiveRun || showLatestRun
  const displayedPercent =
    shouldRenderRun ? (findingsQuery.data?.percent ?? 0) : 0
  const displayedFindingsBase =
    shouldRenderRun ? (findingsQuery.data?.findings ?? []) : []
  const displayedFindings: ScanFindingEventDTO[] =
    displayedFindingsBase.length > 0
      ? displayedFindingsBase
      : (!isTriggering && shouldRenderRun && findingsQuery.data?.isTerminal
        ? [{
            id: 'scan-no-events',
            seq: 0,
            phase: findingsQuery.data.phase === 'failed' ? 'failed' : 'complete',
            level: findingsQuery.data.phase === 'failed' ? 'warn' : 'info',
            message: 'Analysis finished. No detailed step events were recorded for this run.',
            timestamp: new Date().toISOString(),
          }]
        : [])
  const hasTerminalRun = !!findingsQuery.data?.runId && !!findingsQuery.data?.isTerminal
  const showScanPanel = isTriggering || triggerAnalysis.isPending || shouldRenderRun
  const scanPanelTitle = isTriggering || triggerAnalysis.isPending
    ? 'Analyzing repositories...'
    : findingsQuery.data?.isTerminal
      ? 'Analysis results'
      : 'Analysis in progress...'
  const scanPanelFallback = (isTriggering || triggerAnalysis.isPending || !findingsQuery.data?.isTerminal)
    ? 'Preparing analysis...'
    : 'No detailed analysis events were recorded.'
  const finalAnalysisContent =
    contextQuery.data?.status === 'complete'
      ? (contextQuery.data.content ?? '')
      : ''
  const discoveredSystems =
    contextQuery.data?.status === 'complete'
      ? (contextQuery.data.discoveredSystems ?? [])
      : []
  const showFinalAnalysis =
    hasTerminalRun &&
    findingsQuery.data?.phase === 'complete'
  const isAnalysisDirty = showFinalAnalysis && analysisDraft !== finalAnalysisContent

  useEffect(() => {
    if (!showFinalAnalysis) return
    // Don't sync draft while content is still streaming
    if (contextQuery.data?.status === 'streaming') return
    const sourceId = contextQuery.data?.sourceId ?? null
    if (!sourceId) return

    if (analysisDraftSourceId !== sourceId) {
      setAnalysisDraftSourceId(sourceId)
      setAnalysisDraft(finalAnalysisContent)
      setAnalysisUserEdited(false)
      return
    }

    if (!analysisUserEdited && analysisDraft !== finalAnalysisContent) {
      setAnalysisDraft(finalAnalysisContent)
    }
  }, [
    showFinalAnalysis,
    contextQuery.data?.sourceId,
    contextQuery.data?.status,
    analysisDraftSourceId,
    analysisDraft,
    finalAnalysisContent,
    analysisUserEdited,
  ])

  // Auto-open drawer if page loads with an active (non-terminal) run or streaming content
  useEffect(() => {
    if (showScanPanel && !findingsQuery.data?.isTerminal) {
      setDrawerOpen(true)
    }
    if (contextQuery.data?.status === 'streaming') {
      setDrawerOpen(true)
    }
  }, [showScanPanel, findingsQuery.data?.isTerminal, contextQuery.data?.status])

  const handleSaveAnalysis = () => {
    const sourceId = contextQuery.data?.sourceId
    if (!sourceId) return
    updateContextMemoryMutation.mutate(
      { sourceId, content: analysisDraft },
      {
        onSuccess: async () => {
          setAnalysisUserEdited(false)
          toast.success('Analysis updated')
          await qc.invalidateQueries({
            queryKey: trpc.complianceProgram.getContextMemory.queryKey(),
          })
        },
        onError: (err) => {
          toast.error('Failed to save analysis', {
            description: err.message,
          })
        },
      },
    )
  }

  const handleResetAnalysis = () => {
    setAnalysisDraft(finalAnalysisContent)
    setAnalysisUserEdited(false)
  }

  const handleReanalyze = async () => {
    if (!program || connectedRepos.length === 0) return
    setIsTriggering(true)
    setDrawerOpen(true)
    previousRunIdRef.current = findingsQuery.data?.runId ?? null
    triggerStartedAtRef.current = Date.now()
    terminalToastRunIdRef.current = null
    setActiveRunId(null)
    setAnalysisUserEdited(false)

    try {
      for (const repo of connectedRepos) {
        const repoUrl = `https://github.com/${repo.fullName}.git`
        await triggerAnalysis.mutateAsync({
          programId: program.id,
          repositoryUrl: repoUrl,
          branch: repo.defaultBranch ?? 'main',
          repositoryName: repo.fullName,
        })
      }
    } catch (e) {
      console.error('[ConnectRepo] Failed to reanalyze:', e)
      toast.error('Reanalysis failed', {
        description: e instanceof Error ? e.message : 'Unknown error',
      })
    } finally {
      setIsTriggering(false)
      await qc.invalidateQueries({
        queryKey: trpc.complianceProgram.getScanFindings.queryKey(),
      })
      await qc.invalidateQueries({
        queryKey: trpc.complianceProgram.getContextMemory.queryKey(),
      })
    }
  }

  const handleSkip = () => {
    if (!program?.id) return
    skipScanningMutation.mutate(
      { programId: program.id },
      {
        onSuccess: () => {
          qc.invalidateQueries({
            queryKey: trpc.complianceProgram.getProgram.queryKey(),
          })
          navigate({
            to: '/workspaces/$workspaceId/screenata/scope-confirm',
            params: { workspaceId },
          })
        },
      },
    )
  }

  const footer = (
    <>
      {hasConnectedRepos && (
        <button
          onClick={() => {
            if (hasTerminalRun) {
              navigate({
                to: '/workspaces/$workspaceId/screenata/integrations',
                params: { workspaceId },
              })
              return
            }
            handleContinue()
          }}
          disabled={isTriggering || triggerAnalysis.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {(isTriggering || triggerAnalysis.isPending) ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Analyzing...
            </>
          ) : hasTerminalRun ? (
            <>
              Continue
              <ArrowRight className="h-4 w-4" />
            </>
          ) : (
            <>
              Analyze
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      )}
      {program && (
        <button
          onClick={handleSkip}
          disabled={skipScanningMutation.isPending}
          className="text-sm text-muted-foreground underline-offset-2 hover:underline"
        >
          {skipScanningMutation.isPending ? 'Skipping...' : 'Skip'}
        </button>
      )}
    </>
  )

  const handleNextStep = () => {
    if (!hasConnectedRepos) return
    if (hasTerminalRun) {
      navigate({
        to: '/workspaces/$workspaceId/screenata/integrations',
        params: { workspaceId },
      })
      return
    }
    if (!isTriggering && !triggerAnalysis.isPending) {
      handleContinue()
    }
  }

  const handleGoBack = () => {
    navigate({
      to: '/workspaces/$workspaceId/screenata',
      params: { workspaceId },
      search: { step: 99 },
    })
  }

  return (
    <ScreenataStepLayout
      section="Source code"
      title="Connect GitHub"
      description="Connect your repositories so we can analyze your codebase for compliance-relevant infrastructure."
      footer={footer}
      onBack={handleGoBack}
      onNext={hasConnectedRepos ? handleNextStep : undefined}
      disableNext={isTriggering}
    >
      {/* Not configured */}
      {!isConfiguredQuery.isLoading && isConfiguredQuery.data === false && (
        <div className="rounded-xl border p-5">
          <h3 className="font-semibold mb-1">
            GitHub Integration Not Available
          </h3>
          <p className="text-sm text-muted-foreground">
            Contact your administrator to enable GitHub integration.
          </p>
        </div>
      )}

      {/* Install GitHub App */}
      {!isConfiguredQuery.isLoading && isConfiguredQuery.data === true && !hasInstallations && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Install the DeepGuide GitHub App to grant read access to your
            repositories.
          </p>
          <button
            onClick={handleInstallGitHubApp}
            disabled={isInstalling || !installUrlQuery.data}
            className="w-full rounded-xl border-2 border-border px-5 py-4 text-left transition-all hover:border-primary/50 hover:bg-muted/40 disabled:opacity-50 flex items-center gap-4"
          >
            <div className="w-10 h-10 rounded-lg bg-gray-900 text-white flex items-center justify-center shrink-0">
              {isInstalling ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Github className="w-5 h-5" />
              )}
            </div>
            <div>
              <span className="font-medium">
                {isInstalling ? 'Installing...' : 'Install GitHub App'}
              </span>
              <p className="text-xs text-muted-foreground mt-0.5">
                Grants read access to repository contents and metadata
              </p>
            </div>
          </button>
        </div>
      )}

      {/* Loading */}
      {isConfiguredQuery.isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Repository selector using Tags component */}
      {!isConfiguredQuery.isLoading && hasInstallations && (
        <div className="space-y-3">
          {githubAuthIssue && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
              <p className="text-sm font-medium text-amber-900">
                GitHub needs to be reconnected
              </p>
              <p className="mt-1 text-sm text-amber-800">{githubAuthIssue}</p>
              <button
                onClick={handleInstallGitHubApp}
                disabled={isInstalling || !installUrlQuery.data}
                className="mt-3 inline-flex items-center rounded-lg bg-amber-900 px-3 py-2 text-sm font-medium text-amber-50 disabled:opacity-50"
              >
                {isInstalling ? 'Opening...' : 'Reconnect GitHub'}
              </button>
            </div>
          )}

          <div className="flex items-center justify-end">
            <button
              onClick={handleInstallGitHubApp}
              disabled={isInstalling}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              + Add account
            </button>
          </div>

          <Tags>
            <TagsTrigger
              placeholder={hasConnectedRepos ? 'Add more repositories...' : 'Select repositories to connect...'}
              showPlaceholder={!hasConnectedRepos || connectedRepos.length < 3}
              className="rounded-xl min-h-12"
            >
              {connectedRepos.map((repo) => (
                <TagsValue
                  key={repo.fullName}
                  variant="secondary"
                  className="font-mono text-xs"
                  onRemove={() => handleDisconnectRepo(repo.id)}
                >
                  <Github className="w-3 h-3" />
                  {repo.fullName}
                </TagsValue>
              ))}
            </TagsTrigger>
            <TagsContent align="start">
              <TagsInput placeholder="Search repositories..." />
              <TagsList className="max-h-48">
                <TagsEmpty>No repositories found.</TagsEmpty>
                {installations.map((installation) => {
                  const repos = (installation.repositories ?? []).filter((repo: any) => {
                    const linkedWorkspaceIds: string[] = repo.workspaceIds ?? []
                    return !linkedWorkspaceIds.includes(workspaceId)
                  })
                  if (repos.length === 0) return null
                  return (
                    <TagsGroup
                      key={installation.id}
                      heading={installation.accountLogin}
                    >
                      {repos.map((repo: any) => {
                        return (
                          <TagsItem
                            key={repo.id}
                            value={repo.fullName}
                            onSelect={() => {
                              handleConnectRepo(repo.id)
                            }}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {repo.private ? (
                                <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                              ) : (
                                <Globe className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                              )}
                              <span className="font-mono text-sm truncate">
                                {repo.name}
                              </span>
                            </div>
                          </TagsItem>
                        )
                      })}
                    </TagsGroup>
                  )
                })}
              </TagsList>
            </TagsContent>
          </Tags>

          {/* "View Analysis" chip when drawer is closed but there's data */}
          {(showScanPanel || showFinalAnalysis) && !drawerOpen && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {(isTriggering || triggerAnalysis.isPending || (showScanPanel && !findingsQuery.data?.isTerminal)) && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              {hasTerminalRun ? 'View Analysis Results' : 'View Analysis Progress'}
            </button>
          )}

          <AnalysisDrawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            findings={displayedFindings}
            scanPanelTitle={scanPanelTitle}
            scanPanelFallback={scanPanelFallback}
            percent={displayedPercent}
            isTerminal={!!findingsQuery.data?.isTerminal}
            contextContent={contextQuery.data?.content ?? ''}
            contextStatus={contextQuery.data?.status ?? 'none'}
            discoveredSystems={discoveredSystems}
            analysisDraft={analysisDraft}
            onAnalysisDraftChange={(next) => {
              setAnalysisDraft(next)
              setAnalysisUserEdited(next !== finalAnalysisContent)
            }}
            isAnalysisDirty={isAnalysisDirty}
            onSave={handleSaveAnalysis}
            onReset={handleResetAnalysis}
            onReanalyze={handleReanalyze}
            isSaving={updateContextMemoryMutation.isPending}
            isReanalyzing={isTriggering || triggerAnalysis.isPending}
            canReanalyze={connectedRepos.length > 0}
          />
        </div>
      )}
    </ScreenataStepLayout>
  )
}
